if (!customElements.get('rg-pricing-card')) {
  customElements.define(
    'rg-pricing-card',
    class RgPricingCard extends HTMLElement {
      connectedCallback() {
        this.code = (this.dataset.code || '').trim();
        this.pct = parseInt(this.dataset.pct, 10) || 0;
        this.variantId = this.dataset.variantId;
        this.variants = {};
        try {
          JSON.parse(this.querySelector('[data-rg-pc-variants]').textContent).forEach((variant) => {
            this.variants[variant.id] = variant;
          });
        } catch (error) {
          return;
        }

        const locale = document.documentElement.lang || 'en-IN';
        const currency = this.dataset.currency || 'INR';
        // Whole amounts show without decimals; fractional amounts always show two (e.g. 4,465.30).
        const make = (digits) => {
          try {
            return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : locale, {
              style: 'currency',
              currency: currency,
              minimumFractionDigits: digits,
              maximumFractionDigits: 2,
            });
          } catch (error) {
            return null;
          }
        };
        this.formatters = { whole: make(0), fraction: make(2) };

        this.copyButton = this.querySelector('[data-rg-pc-copy]');
        this.copyText = this.querySelector('[data-rg-pc-copy-text]');
        this.status = this.querySelector('[data-rg-pc-status]');
        this.copyButton?.addEventListener('click', this.copy.bind(this));

        // The theme dispatches 'change' on the hidden variant id input as soon as a variant is picked
        // (optimistic update), and publishes variantChange once the section re-render has finished.
        this.productInfo = this.closest('product-info');
        this.productInfo?.addEventListener('change', (event) => {
          const target = event.target;
          if (target && target.name === 'id' && target.matches('input')) this.update(target.value);
        });
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event?.data?.variant;
            if (variant && event.data.sectionId === this.dataset.sectionId) this.update(variant.id);
          });
        }

        // Re-render the server-side amounts with the same INR formatter used on variant changes.
        this.update(this.variantId);
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        clearTimeout(this.feedbackTimer);
      }

      // Amounts are integers in minor units (paise), rounded half up like Shopify percentage discounts.
      money(minor) {
        const amount = minor / 100;
        const formatter = this.formatters[minor % 100 === 0 ? 'whole' : 'fraction'];
        return formatter ? formatter.format(amount) : amount.toFixed(2);
      }

      set(key, text) {
        const element = this.querySelector(`[data-rg-pc="${key}"]`);
        if (element) element.textContent = text;
        return element;
      }

      toggle(key, visible) {
        const element = this.querySelector(`[data-rg-pc="${key}"]`);
        if (element) element.classList.toggle('hidden', !visible);
      }

      update(variantId) {
        const variant = this.variants[variantId];
        if (!variant) return;
        this.variantId = String(variantId);

        const price = variant.price;
        const hasCompare = variant.compare_at_price > price;
        const mrp = hasCompare ? variant.compare_at_price : price;
        const discount = Math.floor((price * this.pct + 50) / 100);
        const final = price - discount;
        const saved = mrp - final;

        this.set('final', this.money(final));
        this.set('total', this.money(final));
        this.set('store-discount', '-' + this.money(mrp - price));
        this.set('discount', '-' + this.money(discount));
        this.set('mrp', this.money(mrp));
        this.set('mrp-strike', this.money(mrp));
        this.set('mrp-off', Math.round((saved * 100) / mrp) + '% off MRP');
        this.set('note', 'Enter the code at checkout. You save ' + this.money(saved) + '.');

        ['mrp-strike', 'mrp-off', 'mrp-row', 'store-row'].forEach((key) => this.toggle(key, hasCompare));

        this.classList.toggle('is-sold-out', !variant.available);
        const note = this.querySelector('[data-rg-pc="note"]');
        const soldOutNote = this.querySelector('[data-rg-pc="soldout-note"]');
        if (note) note.hidden = !variant.available;
        if (soldOutNote) soldOutNote.hidden = variant.available;
      }

      copy() {
        const done = (ok) => this.showFeedback(ok);
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(this.code).then(
            () => done(true),
            () => done(this.fallbackCopy())
          );
        } else {
          done(this.fallbackCopy());
        }
      }

      fallbackCopy() {
        const textarea = document.createElement('textarea');
        textarea.value = this.code;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        let ok = false;
        try {
          ok = document.execCommand('copy');
        } catch (error) {
          ok = false;
        }
        document.body.removeChild(textarea);
        return ok;
      }

      showFeedback(ok) {
        const message = ok ? 'Coupon copied!' : "Couldn't copy automatically. Your code is " + this.code;
        const original = this.copyText.innerHTML;
        this.copyButton.classList.toggle('is-copied', ok);
        this.copyText.textContent = message;
        if (this.status) this.status.textContent = message;
        clearTimeout(this.feedbackTimer);
        this.feedbackTimer = setTimeout(() => {
          this.copyButton.classList.remove('is-copied');
          this.copyText.innerHTML = original;
          if (this.status) this.status.textContent = '';
        }, 2500);
      }
    }
  );
}
